import React, { useEffect, useRef, useState } from "react";
import {
  MdChatBubbleOutline,
  MdRemove,
  MdSend,
} from "react-icons/md";
import { ConversationEngine, content } from "./conversationEngine";
import { getCreativeImage } from "./creativeAssets";
import "./ConversationalAssistant.css";

const ALLOWED_TAGS = new Set([
  "p",
  "ul",
  "ol",
  "li",
  "b",
  "strong",
  "em",
  "i",
  "sup",
  "table",
  "tbody",
  "tr",
  "th",
  "td",
  "div",
  "br",
]);

const ESCAPES = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#039;",
};

const escapeHtml = (value) =>
  value.replace(/[&<>"']/g, (character) => ESCAPES[character]);

const renderRichHtml = (html, visibleCharacters = Infinity) => {
  const documentNode = new DOMParser().parseFromString(
    `<body>${html}</body>`,
    "text/html"
  );
  let remaining = visibleCharacters;

  const renderNode = (node) => {
    if (remaining <= 0) {
      return "";
    }

    if (node.nodeType === 3) {
      const text = node.nodeValue || "";
      const shown = text.slice(0, remaining);
      remaining -= shown.length;
      return escapeHtml(shown);
    }

    if (node.nodeType !== 1) {
      return "";
    }

    const tag = node.localName;
    const children = [...node.childNodes].map(renderNode).join("");
    if (!ALLOWED_TAGS.has(tag)) {
      return children;
    }

    if (tag === "br") {
      return "<br>";
    }

    const className =
      tag === "div" && node.classList.contains("isi")
        ? ' class="isi"'
        : "";
    return `<${tag}${className}>${children}</${tag}>`;
  };

  return [...documentNode.body.childNodes].map(renderNode).join("");
};

const getRichTextLength = (html) => {
  const documentNode = new DOMParser().parseFromString(
    `<body>${html}</body>`,
    "text/html"
  );
  return (documentNode.body.textContent || "").length;
};

const brandVariables = (brandKey) => {
  const brand = content.brands[brandKey];
  return {
    "--ca-brand": brand.color,
    "--ca-brand-soft": brand.soft,
    "--ca-brand-border": `${brand.color}52`,
    "--ca-brand-tile": brand.tile,
    "--c": brand.color,
    "--bs": brand.soft,
    "--bd": `${brand.color}52`,
    "--tile": brand.tile,
  };
};

const BrandTile = ({ brandKey, compact = false }) => (
  <span
    className={`bt ${compact ? "compact" : ""}`}
    style={brandVariables(brandKey)}
    aria-hidden="true"
  >
    {content.brands[brandKey].name.slice(0, 2).toUpperCase()}
  </span>
);

const BrandWordmark = ({ brandKey }) => (
  <span className={`wm wm-${brandKey}`}>{content.brands[brandKey].name}</span>
);

const InfoButton = ({ onClick, label = "Why am I seeing this ad?" }) => (
  <button
    type="button"
    className="why"
    aria-label={label}
    title={label}
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
  >
    i
  </button>
);

const RichText = ({ html, visibleCharacters }) => (
  <div
    className="answer"
    dangerouslySetInnerHTML={{
      __html: renderRichHtml(html, visibleCharacters),
    }}
  />
);

const Sources = ({ sourceKeys }) => {
  if (!sourceKeys || !sourceKeys.length) {
    return null;
  }

  return (
    <details className="sources">
      <summary className="src-t">
        {sourceKeys.length} source{sourceKeys.length === 1 ? "" : "s"}
      </summary>
      <ol>
        {sourceKeys.map((sourceKey) => (
          <li key={sourceKey}>{content.sources[sourceKey]}</li>
        ))}
      </ol>
    </details>
  );
};

const SponsoredLabel = ({ onWhy }) => (
  <span className="adl">
    Sponsored
    <InfoButton onClick={onWhy} />
  </span>
);

const CreativeUnit = ({ creativeId, decision, onWhy, onClickOut }) => {
  const creative = content.creatives[creativeId];
  const brand = content.brands[creative.brand];
  const variables = brandVariables(creative.brand);

  if (creative.format === "resources") {
    return (
      <div className="rs" style={variables}>
        <div className="rs-h">
          Sponsored Resources
          <InfoButton onClick={() => onWhy(decision)} />
        </div>
        <div className="rs-c">
          {creative.copy} From {brand.name} · {brand.domain}
        </div>
        {creative.items.map((item) => (
          <button
            type="button"
            className="rs-i"
            key={item.url}
            onClick={() => onClickOut(item.url)}
          >
            <BrandTile brandKey={creative.brand} />
            <span>
              <span className="rs-t">{item.title} ↗</span>
              <span className="rs-d">{item.desc}</span>
            </span>
          </button>
        ))}
      </div>
    );
  }

  if (creative.format === "card") {
    const image = getCreativeImage(brand.imageAsset);
    return (
      <div
        className="nc"
        style={variables}
        role="button"
        tabIndex={0}
        onClick={() => onClickOut(creative.url)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClickOut(creative.url);
          }
        }}
      >
        <span
          className="nc-th"
          style={image ? { backgroundImage: `url("${image}")` } : undefined}
        />
        <span className="nc-copy">
          <BrandWordmark brandKey={creative.brand} />
          <span className="nc-t">{creative.headline}</span>
          <span className="nc-d">{creative.desc}</span>
        </span>
        <SponsoredLabel onWhy={() => onWhy(decision)} />
      </div>
    );
  }

  if (creative.format === "stat") {
    return (
      <div className="stc" style={variables}>
        <SponsoredLabel onWhy={() => onWhy(decision)} />
        <button
          type="button"
          className="stc-b"
          onClick={() => onClickOut(creative.url)}
        >
          <BrandWordmark brandKey={creative.brand} />
          <span className="stc-n">{creative.stat}</span>
          <span className="stc-l">{creative.statLabel}</span>
          <span className="stc-d">{creative.desc}</span>
        </button>
        <div className="stc-f">
          <button
            type="button"
            onClick={() => onClickOut(creative.ctaUrl)}
          >
            {creative.cta} →
          </button>
          {" · "}
          <button
            type="button"
            onClick={() => onClickOut(`${brand.domain}/isi`)}
          >
            Important Safety Information
          </button>
        </div>
      </div>
    );
  }

  if (creative.format === "display") {
    const image = getCreativeImage(brand.imageAsset);
    const displayStyle = {
      ...variables,
      "--ca-display-bg": brand.ds.bg,
      "--ca-display-fg": brand.ds.fg,
      "--ca-cta-bg": brand.ds.cta,
      "--ca-cta-fg": brand.ds.ctaFg,
      "--dsbg": brand.ds.bg,
      "--dsfg": brand.ds.fg,
      "--ctabg": brand.ds.cta,
      "--ctafg": brand.ds.ctaFg,
    };

    return (
      <div className="ds-wrap" style={displayStyle}>
        <div className="ds-l">
          <SponsoredLabel onWhy={() => onWhy(decision)} />
        </div>
        <div className="ds-unit">
          <button
            type="button"
            className="ds"
            onClick={() => onClickOut(creative.url)}
          >
            {image && (
              <span
                className="ds-img"
                style={{ backgroundImage: `url("${image}")` }}
              />
            )}
            <span className="ds-tx">
              <BrandWordmark brandKey={creative.brand} />
              <span className="ds-h">{creative.headline}</span>
            </span>
            <span className="ds-cta">{creative.cta}</span>
          </button>
          {creative.isi && (
            <button
              type="button"
              className="ds-isi"
              style={{
                background: brand.ds.isi,
                color: brand.ds.isiFg,
              }}
              onClick={() => onClickOut(`${brand.domain}/isi`)}
            >
              {creative.isi}
            </button>
          )}
        </div>
      </div>
    );
  }

  return null;
};

const AssistantMessage = ({
  message,
  environment,
  onWhy,
  onClickOut,
}) => {
  if (message.role === "system") {
    return <div className="sysmsg">{message.text}</div>;
  }

  if (message.role === "user") {
    return (
      <div className="m-q">
        <div className="bub">{message.text}</div>
      </div>
    );
  }

  const resources = (message.decision?.served || []).filter(
    (creativeId) => content.creatives[creativeId].format === "resources"
  );
  const units = (message.decision?.served || []).filter((creativeId) =>
    ["card", "stat", "display"].includes(
      content.creatives[creativeId].format
    )
  );

  return (
    <div
      className={`m m-a ${message.sponsoredBrand ? "spa" : ""}`}
      style={
        message.sponsoredBrand
          ? brandVariables(message.sponsoredBrand)
          : undefined
      }
    >
      <div className="bubble">
        {message.sponsoredBrand && (
          <div className="spa-h">
            <BrandTile brandKey={message.sponsoredBrand} compact />
            <b>{content.brands[message.sponsoredBrand].name}</b>
            {message.decision && (
              <SponsoredLabel onWhy={() => onWhy(message.decision)} />
            )}
          </div>
        )}
        {message.phase === "thinking" && (
          <div className="thinking">
            <i />
            <i />
            <i />
            <span>{message.thinking}…</span>
          </div>
        )}
        {message.html && (
          <RichText
            html={message.html}
            visibleCharacters={
              message.phase === "streaming"
                ? message.visibleCharacters
                : Infinity
            }
          />
        )}
        {resources.map((creativeId) => (
          <CreativeUnit
            key={creativeId}
            creativeId={creativeId}
            decision={message.decision}
            onWhy={onWhy}
            onClickOut={onClickOut}
          />
        ))}
        <Sources sourceKeys={message.sources} />
        {message.approval && (
          <div className="approved">{message.approval}</div>
        )}
        {environment === "ehr" && message.timestamp && (
          <span className="aa-time">{message.timestamp}</span>
        )}
      </div>
      {environment === "web" && message.phase === "complete" && (
        <div className="m-fb">
          <span>♡</span>
          <span>♧</span>
          <span>○</span>
          <span>A moment ago</span>
        </div>
      )}
      <div className={`spz ${units.length ? "in" : ""}`}>
        {units.map((creativeId) => (
          <CreativeUnit
            key={creativeId}
            creativeId={creativeId}
            decision={message.decision}
            onWhy={onWhy}
            onClickOut={onClickOut}
          />
        ))}
      </div>
    </div>
  );
};

const PromptButton = ({ creativeId, environment, onClick }) => {
  const creative = content.creatives[creativeId];
  const brand = content.brands[creative.brand];
  const className = environment === "ehr" ? "aa-pill" : "pill";

  return (
    <button
      type="button"
      className={`${className} spons`}
      style={brandVariables(creative.brand)}
      onClick={() => onClick(creativeId)}
    >
      <BrandTile brandKey={creative.brand} compact />
      <span>
        {creative.text}
        <span className="sl">Sponsored · {brand.name}</span>
      </span>
    </button>
  );
};

const SuggestionTray = ({
  environment,
  suggestions,
  sponsoredBrand,
  onQuestion,
  onPrompt,
  onSponsoredTopic,
  onExitSponsored,
}) => {
  if (!suggestions.questionKeys.length && !suggestions.promptCreativeId) {
    return null;
  }

  const className = environment === "ehr" ? "aa-sugg" : "pills";
  const buttonClass = environment === "ehr" ? "aa-pill" : "pill";

  return (
    <div className={className}>
      {suggestions.mode === "sponsored"
        ? suggestions.questionKeys.map((topic) => (
            <button
              type="button"
              className={`${buttonClass} spx`}
              style={brandVariables(sponsoredBrand)}
              key={topic}
              onClick={() => onSponsoredTopic(topic)}
            >
              {content.sponsored[sponsoredBrand].answers[topic].q}
            </button>
          ))
        : suggestions.questionKeys.map((questionKey) => (
            <button
              type="button"
              className={buttonClass}
              key={questionKey}
              onClick={() => onQuestion(questionKey)}
            >
              {environment === "ehr"
                ? content.questions[questionKey].short
                : content.questions[questionKey].text}
            </button>
          ))}
      {suggestions.promptCreativeId && (
        <PromptButton
          creativeId={suggestions.promptCreativeId}
          environment={environment}
          onClick={onPrompt}
        />
      )}
      {suggestions.mode === "sponsored" && (
        <button
          type="button"
          className={`${buttonClass} exit`}
          onClick={onExitSponsored}
        >
          Exit sponsored conversation
        </button>
      )}
    </div>
  );
};

const WhyDialog = ({ decision, environment, onClose }) => {
  if (!decision || !decision.brand) {
    return null;
  }

  const brand = content.brands[decision.brand];
  const lines =
    decision.kind === "sponsored"
      ? [
          "You opened this conversation from a Sponsored Prompt",
          `Answers come only from ${brand.lib}`,
        ]
      : [
          `Matched to your question on ${(
            decision.area || "relevant clinical content"
          ).toLowerCase()}`,
          "Shown only to verified HCPs (NPI matched)",
        ];

  if (environment === "ehr") {
    lines.push("No patient data from the chart is used");
  }

  return (
    <div className="wp on" onMouseDown={onClose}>
      <div
        className="wp-c"
        role="dialog"
        aria-modal="true"
        aria-label="Why you're seeing this ad"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="wp-h">
          <span className="why-static">i</span>
          <b>Why you're seeing this ad</b>
          <button
            type="button"
            className="wp-x"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="wp-s">
          <BrandTile brandKey={decision.brand} compact />
          <span>
            Sponsored by <b>{brand.name}</b>
          </span>
        </div>
        <ul className="wp-l">
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const SponsoredBar = ({ brandKey, onExit }) => {
  if (!brandKey) {
    return null;
  }

  const brand = content.brands[brandKey];
  return (
    <div className="sp-bar on" style={brandVariables(brandKey)}>
      <BrandTile brandKey={brandKey} compact />
      <div className="t">
        <b>Sponsored conversation · {brand.name}</b>
        Answers only from {brand.lib}
      </div>
      <button type="button" className="x" onClick={onExit}>
        Exit
      </button>
    </div>
  );
};

const timeNow = () =>
  new Date().toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

const ConversationalAssistant = ({ environment, onOpenChange }) => {
  const engineRef = useRef(null);
  if (
    !engineRef.current ||
    engineRef.current.environment !== environment
  ) {
    engineRef.current = new ConversationEngine(environment);
  }

  const engine = engineRef.current;
  const opening = engine.getOpening();
  const [isOpen, setIsOpen] = useState(environment === "web");
  const [isWide, setIsWide] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [sponsoredBrand, setSponsoredBrand] = useState(null);
  const [whyDecision, setWhyDecision] = useState(null);
  const [notice, setNotice] = useState(null);
  const [messages, setMessages] = useState([
    {
      id: "opening",
      role: "assistant",
      phase: "complete",
      html: `<p>${opening.greeting}</p>`,
      timestamp: timeNow(),
    },
  ]);
  const [suggestions, setSuggestions] = useState({
    mode: "organic",
    questionKeys: opening.questionKeys,
    promptCreativeId: opening.promptCreativeId,
  });
  const activeRef = useRef(true);
  const timersRef = useRef(new Set());
  const framesRef = useRef(new Set());
  const scrollRef = useRef(null);
  const noticeTimerRef = useRef(null);

  useEffect(() => {
    const timers = timersRef.current;
    const frames = framesRef.current;
    activeRef.current = true;
    return () => {
      activeRef.current = false;
      timers.forEach(clearTimeout);
      frames.forEach(cancelAnimationFrame);
      clearTimeout(noticeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, suggestions]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== "Escape") {
        return;
      }
      if (whyDecision) {
        setWhyDecision(null);
      } else if (
        environment === "ehr" &&
        isOpen &&
        !event.target.matches("input")
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [environment, isOpen, whyDecision]);

  const delay = (milliseconds) =>
    new Promise((resolve) => {
      const timer = setTimeout(() => {
        timersRef.current.delete(timer);
        resolve();
      }, milliseconds);
      timersRef.current.add(timer);
    });

  const updateMessage = (id, changes) => {
    setMessages((current) =>
      current.map((message) =>
        message.id === id ? { ...message, ...changes } : message
      )
    );
  };

  const streamMessage = (id, html) =>
    new Promise((resolve) => {
      const total = getRichTextLength(html);
      let visibleCharacters = 0;
      let carry = 0;

      const tick = () => {
        if (!activeRef.current) {
          resolve();
          return;
        }

        carry += 3.4;
        const increment = Math.max(1, Math.floor(carry));
        carry -= increment;
        visibleCharacters = Math.min(
          total,
          visibleCharacters + increment
        );
        updateMessage(id, { visibleCharacters });

        if (visibleCharacters >= total) {
          resolve();
          return;
        }

        const frame = requestAnimationFrame(() => {
          framesRef.current.delete(frame);
          tick();
        });
        framesRef.current.add(frame);
      };

      tick();
    });

  const runTurn = async (turn) => {
    if (!turn) {
      return;
    }

    const assistantId = `assistant-${turn.id}`;
    const brand = turn.kind === "sponsored" ? turn.brand : null;
    const sponsored = brand ? content.brands[brand] : null;
    const answerHtml = turn.answer
      ? turn.answer.html
      : `<p>I can only answer from ${sponsored.lib}. For anything else, exit to ${content.envs[environment].assistant}, or contact ${sponsored.contact}.</p>`;

    if (turn.exitedSponsored) {
      setMessages((current) => [
        ...current,
        {
          id: `exit-${turn.id}`,
          role: "system",
          text: `Sponsored conversation ended. You're back in ${content.envs[environment].assistant}.`,
        },
      ]);
      setSponsoredBrand(null);
    }

    if (turn.entered) {
      setSponsoredBrand(turn.brand);
      setMessages((current) => [
        ...current,
        {
          id: `enter-${turn.id}`,
          role: "system",
          text: `You started a sponsored conversation with ${sponsored.name}.`,
        },
      ]);
    }

    setBusy(true);
    setSuggestions({
      mode: turn.kind,
      questionKeys: [],
      promptCreativeId: null,
    });
    setMessages((current) => [
      ...current,
      {
        id: `user-${turn.id}`,
        role: "user",
        text: turn.questionText,
      },
      {
        id: assistantId,
        role: "assistant",
        phase: "thinking",
        thinking:
          turn.kind === "sponsored"
            ? "Retrieving approved content"
            : turn.answer.thinking,
        sponsoredBrand: brand,
        timestamp: timeNow(),
      },
    ]);

    try {
      await delay(turn.kind === "sponsored" ? 700 : 900);
      if (!activeRef.current) {
        return;
      }

      updateMessage(assistantId, {
        phase: "streaming",
        html: answerHtml,
        visibleCharacters: 0,
      });
      await streamMessage(assistantId, answerHtml);
      if (!activeRef.current) {
        return;
      }

      const result = engine.completeTurn(turn);
      if (turn.kind === "organic") {
        await delay(380);
      }
      if (!activeRef.current) {
        return;
      }

      const approval =
        turn.kind === "sponsored"
          ? turn.answer
            ? `${sponsored.lib} · ${sponsored.code}`
            : "Not covered by approved content, so no answer was generated"
          : null;

      updateMessage(assistantId, {
        phase: "complete",
        decision: result.decision,
        sources: turn.answer ? turn.answer.sources : [],
        approval,
        visibleCharacters: Infinity,
      });
      setSuggestions({
        mode: turn.kind,
        questionKeys: result.questionKeys,
        promptCreativeId: result.promptCreativeId,
      });
    } finally {
      engine.finishTurn();
      if (activeRef.current) {
        setBusy(false);
      }
    }
  };

  const handleQuestion = (questionKey) => {
    runTurn(engine.beginQuestion(questionKey));
  };

  const handlePrompt = (creativeId) => {
    runTurn(engine.beginPrompt(creativeId));
  };

  const handleSponsoredTopic = (topic) => {
    runTurn(engine.beginSponsoredTopic(topic));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const value = inputValue.trim();
    if (!value || busy) {
      return;
    }
    setInputValue("");
    runTurn(engine.beginTypedQuestion(value));
  };

  const handleExitSponsored = () => {
    const brand = engine.exitSponsoredConversation();
    if (!brand) {
      return;
    }

    setSponsoredBrand(null);
    setMessages((current) => [
      ...current,
      {
        id: `exit-${Date.now()}`,
        role: "system",
        text: `Sponsored conversation ended. You're back in ${content.envs[environment].assistant}.`,
      },
    ]);
    setSuggestions({
      mode: "organic",
      questionKeys: engine.getAvailableQuestionKeys(),
      promptCreativeId: null,
    });
  };

  const handleClickOut = (destination) => {
    setNotice({
      title: `Opens https://${destination}`,
      body: "In a new tab; the conversation stays open.",
    });
    clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => setNotice(null), 3600);
  };

  const panelMessages = (
    <>
      {messages.map((message) => (
        <AssistantMessage
          key={message.id}
          message={message}
          environment={environment}
          onWhy={setWhyDecision}
          onClickOut={handleClickOut}
        />
      ))}
      {environment === "web" && (
        <SuggestionTray
          environment={environment}
          suggestions={suggestions}
          sponsoredBrand={sponsoredBrand}
          onQuestion={handleQuestion}
          onPrompt={handlePrompt}
          onSponsoredTopic={handleSponsoredTopic}
          onExitSponsored={handleExitSponsored}
        />
      )}
    </>
  );

  return (
    <div className="ca-scope ca-overlay">
      <section
        className={`frame ${environment} on ${
          environment === "ehr"
            ? isOpen
              ? ""
              : "aa-closed"
            : isOpen
            ? ""
            : "agent-closed"
        }`}
      >
        {environment === "ehr" ? (
          <>
            <div className="aa" role="dialog" aria-label="Clinical Assistant">
              <div className="aa-head">
                <span className="aa-ic">
                  <MdChatBubbleOutline />
                </span>
                <div>
                  <h3>Clinical Assistant</h3>
                  <p>Always here to help</p>
                </div>
                <button
                  type="button"
                  className="aa-min"
                  aria-label="Minimize"
                  onClick={() => setIsOpen(false)}
                >
                  <MdRemove />
                </button>
              </div>
              <SponsoredBar
                brandKey={sponsoredBrand}
                onExit={handleExitSponsored}
              />
              <div className="aa-msgs" ref={scrollRef}>
                <div className="thread">{panelMessages}</div>
              </div>
              <SuggestionTray
                environment={environment}
                suggestions={suggestions}
                sponsoredBrand={sponsoredBrand}
                onQuestion={handleQuestion}
                onPrompt={handlePrompt}
                onSponsoredTopic={handleSponsoredTopic}
                onExitSponsored={handleExitSponsored}
              />
              <form className="aa-in" onSubmit={handleSubmit}>
                <input
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  placeholder={
                    sponsoredBrand
                      ? content.brands[sponsoredBrand].ask ||
                        `Ask about ${content.brands[sponsoredBrand].name}…`
                      : "Ask about dosing, guidelines or evidence…"
                  }
                  aria-label="Message"
                  disabled={busy}
                />
                <button
                  type="submit"
                  className="aa-send"
                  aria-label="Send"
                  disabled={busy || !inputValue.trim()}
                >
                  <MdSend />
                </button>
              </form>
              <WhyDialog
                decision={whyDecision}
                environment={environment}
                onClose={() => setWhyDecision(null)}
              />
            </div>
            <button
              type="button"
              className="aa-launch"
              aria-label="Open Clinical Assistant"
              onClick={() => setIsOpen(true)}
            >
              <MdChatBubbleOutline />
            </button>
          </>
        ) : (
          <>
            <div
              className={`agent ${isWide ? "wide" : ""}`}
              role="dialog"
              aria-label="Rounds Review Agent"
            >
              <div className="ag-head">
                <span className="ag-av">
                  <i />
                  <b>RR</b>
                </span>
                <div className="ag-name">Rounds Review Agent</div>
                <span className="ag-dot" />
                <button
                  type="button"
                  className="ag-ic"
                  aria-label={isWide ? "Restore width" : "Expand"}
                  onClick={() => setIsWide((current) => !current)}
                >
                  ⤢
                </button>
                <button
                  type="button"
                  className="ag-ic"
                  aria-label="Close"
                  onClick={() => setIsOpen(false)}
                >
                  ×
                </button>
              </div>
              <SponsoredBar
                brandKey={sponsoredBrand}
                onExit={handleExitSponsored}
              />
              <div className="ag-scroll" ref={scrollRef}>
                <div className="thread">{panelMessages}</div>
              </div>
              <form className="ag-in" onSubmit={handleSubmit}>
                <button
                  type="button"
                  className="ag-plus"
                  aria-label="Attach"
                >
                  +
                </button>
                <div className="ag-box">
                  <input
                    value={inputValue}
                    onChange={(event) =>
                      setInputValue(event.target.value)
                    }
                    placeholder={
                      sponsoredBrand
                        ? content.brands[sponsoredBrand].ask ||
                          `Ask about ${content.brands[sponsoredBrand].name}…`
                        : "Type your message here"
                    }
                    aria-label="Message"
                    disabled={busy}
                  />
                  <button
                    type="submit"
                    className="ag-send"
                    aria-label="Send"
                    disabled={busy || !inputValue.trim()}
                  >
                    <MdSend />
                  </button>
                </div>
              </form>
              <div className="ag-foot">
                <div>
                  By continuing you agree to our:
                  <b>Terms of Service</b>
                  <b>Disclaimer</b>
                </div>
                <div>© roundsreview-en 2026</div>
              </div>
              <WhyDialog
                decision={whyDecision}
                environment={environment}
                onClose={() => setWhyDecision(null)}
              />
            </div>
            <button
              type="button"
              className="ag-launch"
              aria-label="Open Rounds Review Agent"
              onClick={() => setIsOpen(true)}
            >
              <span className="ag-av">
                <i />
                <b>RR</b>
              </span>
            </button>
          </>
        )}
        {notice && (
          <div className="toast on" role="status">
            <b>{notice.title}</b>
            {notice.body}
          </div>
        )}
      </section>
    </div>
  );
};

export default ConversationalAssistant;
